import React, { useState, useEffect, useMemo } from 'react';
import { api } from '../services/api';
import { useAuth } from './AuthContext';
import InvitationCodeManager from './InvitationCodeManager';
import ManualReservation from './ManualReservation';
import AppHeader from './AppHeader';
import { toast, confirm as swalConfirm } from '../utils/swal';
import { formatCurrency } from '../utils/format';
import './AdminPanel.css';

const AdminPanel = ({ onBack, onGoToProfile, onGoToEventManagement, onGoToVenueManage, onGoToQrScanner }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'Admin';

  const [activeTab, setActiveTab]                 = useState('reservations');
  const [reservations, setReservations]           = useState([]);
  const [events, setEvents]                       = useState([]);
  const [venues, setVenues]                       = useState([]);
  const [venueMap, setVenueMap]                   = useState({});
  const [loading, setLoading]                     = useState(true);
  const [managingCodesFor, setManagingCodesFor]   = useState(null);
  const [manualReservationFor, setManualReservationFor] = useState(null); // { event, venue }
  const [payingReservation, setPayingReservation] = useState(null);

  // Kullanıcı yönetimi
  const [users, setUsers]               = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [userSearch, setUserSearch]     = useState('');
  const [showAddUser, setShowAddUser]   = useState(false);
  const [editingRole, setEditingRole]   = useState(null); // { id, role }
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  // Aktif kullanıcı sayısı
  const [activeUsers, setActiveUsers] = useState(null);

  const [filterEvent, setFilterEvent]             = useState('all');
  const [filterStatus, setFilterStatus]           = useState('active');
  const [filterPayment, setFilterPayment]         = useState('all');
  const [filterManual, setFilterManual]           = useState('all'); // 'all' | 'manual' | 'online'
  const [filterSearch, setFilterSearch]           = useState('');
  const [collapsed, setCollapsed]                 = useState({});

  // İstatistik filtreleri
  const [statEvent, setStatEvent]         = useState('all');
  const [statStatus, setStatStatus]       = useState('all');
  const [statPayment, setStatPayment]     = useState('all');
  const [statPayMethod, setStatPayMethod] = useState('all');
  const [statManual, setStatManual]       = useState('all'); // 'all' | 'manual' | 'online'
  const [statDateFrom, setStatDateFrom]   = useState('');
  const [statDateTo, setStatDateTo]       = useState('');

  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 20;

  const headerNavProps = {
    onGoToProfile,
    onGoToEventManagement,
    onGoToAdminPanel: () => {},
    onGoToVenueManage: isAdmin ? onGoToVenueManage : undefined,
    onGoToQrScanner,
  };

  // ── loadData — tüm return'lerden ve useMemo'lardan ÖNCE tanımlanmalı ──
  const loadData = async () => {
    setLoading(true);
    try {
      const [resData, eventsData, venuesData] = await Promise.all([
        api.getReservations(), api.getEvents(), api.getVenues()
      ]);
      setReservations(Array.isArray(resData) ? resData : []);
      setEvents(Array.isArray(eventsData) ? eventsData : []);
      setVenues(Array.isArray(venuesData) ? venuesData : []);
      const map = {};
      (Array.isArray(venuesData) ? venuesData : []).forEach(v => { map[v.id] = v.name; });
      setVenueMap(map);
    } catch (err) {
      console.error('Veri yükleme hatası:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    setUsersLoading(true);
    try {
      const token = localStorage.getItem('gg_access_token');
      const res = await fetch(`${API_URL}/api/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setUsers(await res.json());
    } catch (err) {
      console.error('Kullanıcı yükleme hatası:', err);
    } finally {
      setUsersLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);
  useEffect(() => { if (activeTab === 'users') loadUsers(); }, [activeTab]);

  useEffect(() => {
    const fetchActive = async () => {
      try {
        const token = localStorage.getItem('gg_access_token');
        const res = await fetch(`${API_URL}/api/admin/active-users`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setActiveUsers(data.activeUsers);
        }
      } catch {}
    };
    fetchActive();
    const interval = setInterval(fetchActive, 60000); // her 1 dk'da bir güncelle
    return () => clearInterval(interval);
  }, []);

  // ── useMemo'lar erken return'lerden ÖNCE ──
  const filtered = useMemo(() => {
    return reservations.filter(r => {
      if (filterEvent !== 'all' && r.eventId !== filterEvent) return false;
      if (filterStatus === 'active'    && r.status === 2) return false;
      if (filterStatus === 'cancelled' && r.status !== 2) return false;
      if (filterPayment === 'paid'    && !r.paymentReceived) return false;
      if (filterPayment === 'pending' && (r.paymentReceived || r.status === 2)) return false;
      if (filterManual === 'manual' && !r.isManual) return false;
      if (filterManual === 'online' && r.isManual) return false;
      if (filterSearch) {
        const q = filterSearch.toLowerCase();
        const seats = (r.seats || []).map(s => s.seatId).join(' ').toLowerCase();
        if (!r.customerName?.toLowerCase().includes(q) &&
            !r.customerEmail?.toLowerCase().includes(q) &&
            !seats.includes(q)) return false;
      }
      return true;
    });
  }, [reservations, filterEvent, filterStatus, filterPayment, filterManual, filterSearch]);

  const grouped = useMemo(() => {
    const map = {};
    filtered.forEach(r => {
      if (!map[r.eventId]) map[r.eventId] = { eventName: r.eventName, reservations: [] };
      map[r.eventId].reservations.push(r);
    });
    return Object.entries(map);
  }, [filtered]);

  const filteredStats = useMemo(() => {
    return reservations.filter(r => {
      if (statEvent !== 'all' && r.eventId !== statEvent) return false;
      if (statStatus === 'active'    && r.status === 2) return false;
      if (statStatus === 'cancelled' && r.status !== 2) return false;
      if (statPayment === 'paid'    && !r.paymentReceived) return false;
      if (statPayment === 'pending' && r.paymentReceived) return false;
      if (statPayMethod !== 'all'   && parseInt(r.paymentMethod) !== parseInt(statPayMethod)) return false;
      if (statManual === 'manual' && !r.isManual) return false;
      if (statManual === 'online' && r.isManual) return false;
      if (statDateFrom && new Date(r.reservationDate) < new Date(statDateFrom)) return false;
      if (statDateTo) {
        const to = new Date(statDateTo); to.setHours(23, 59, 59);
        if (new Date(r.reservationDate) > to) return false;
      }
      return true;
    });
  }, [reservations, statEvent, statStatus, statPayment, statPayMethod, statManual, statDateFrom, statDateTo]);

  // ── Erken return'ler ──
  if (managingCodesFor) {
    return (
      <InvitationCodeManager
        event={managingCodesFor}
        onBack={() => setManagingCodesFor(null)}
        {...headerNavProps}
      />
    );
  }

  if (manualReservationFor) {
    return (
      <ManualReservation
        event={manualReservationFor.event}
        venue={manualReservationFor.venue}
        onBack={() => setManualReservationFor(null)}
        onComplete={() => {
          setManualReservationFor(null);
          loadData();
          toast.success('Manuel rezervasyon kaydedildi!');
        }}
        {...headerNavProps}
      />
    );
  }

  const handleManualReservation = (event) => {
    const venue = venues.find(v => v.id === event.venueId);
    if (!venue) { toast.error('Salon bilgisi bulunamadı!'); return; }
    setManualReservationFor({ event, venue });
  };

  const toggleGroup = (eventId) =>
    setCollapsed(prev => ({ ...prev, [eventId]: !prev[eventId] }));

  const getPaymentLabel = (r) => {
    if (r.paymentReceived) {
      const m = { 0:'Nakit', 1:'Kredi Kartı', 2:'Havale', 3:'Online', 4:'Ücretsiz' };
      return { text: m[r.paymentMethod] || 'Ödendi', cls: 'paid' };
    }
    return { text: 'Bekliyor', cls: 'pending' };
  };

  const getStatusLabel = (status) => {
    const key = parseInt(status);
    const s = { 0:'Beklemede', 1:'Onaylandı', 2:'İptal', 3:'Tamamlandı', 4:'Onaylandı' };
    const c = { 0:'pending',   1:'confirmed',  2:'cancelled', 3:'completed', 4:'confirmed' };
    return { text: s[key] ?? 'Onaylandı', cls: c[key] ?? 'confirmed' };
  };

  const handleCancel = async (reservation) => {
    const result = await swalConfirm.action(
      'Rezervasyonu İptal Et',
      `${reservation.customerName} — ${reservation.eventName} rezervasyonunu iptal etmek istediğinize emin misiniz?`,
      'Evet, İptal Et'
    );
    if (!result.isConfirmed) return;
    try {
      await api.cancelReservation(reservation.id, user?.fullName || 'Yönetici');
      toast.success('Rezervasyon iptal edildi.');
      loadData();
    } catch { toast.error('İptal işlemi başarısız!'); }
  };

  const CANCELLED    = 2;
  const activeCount    = reservations.filter(r => r.status !== CANCELLED).length;
  const cancelledCount = reservations.filter(r => r.status === CANCELLED).length;
  const pendingCount   = reservations.filter(r => !r.paymentReceived && r.status !== CANCELLED).length;
  const manualCount    = reservations.filter(r => r.isManual && r.status !== CANCELLED).length;

  if (loading) {
    return (
      <div className="admin-panel">
        <AppHeader title={isAdmin ? 'Admin Paneli' : 'Etkinlik Paneli'} onBack={onBack} {...headerNavProps} />
        <div className="loading"><i className="fa-solid fa-spinner fa-spin" /> Yükleniyor...</div>
      </div>
    );
  }

  return (
    <div className="admin-panel">
      <AppHeader title={isAdmin ? 'Admin Paneli' : 'Etkinlik Paneli'} onBack={onBack} {...headerNavProps} />

      <div className="admin-tabs">
        <button className={`tab-btn ${activeTab === 'reservations' ? 'active' : ''}`} onClick={() => setActiveTab('reservations')}>
          <i className="fa-solid fa-list-check" /> Rezervasyonlar ({activeCount})
        </button>
        <button className={`tab-btn ${activeTab === 'events' ? 'active' : ''}`} onClick={() => setActiveTab('events')}>
          <i className="fa-solid fa-calendar-days" /> Etkinlikler ({events.length})
        </button>
        {isAdmin && (
          <button className={`tab-btn ${activeTab === 'stats' ? 'active' : ''}`} onClick={() => setActiveTab('stats')}>
            <i className="fa-solid fa-chart-bar" /> İstatistikler
          </button>
        )}
        {isAdmin && (
          <button className={`tab-btn ${activeTab === 'users' ? 'active' : ''}`} onClick={() => setActiveTab('users')}>
            <i className="fa-solid fa-users" /> Kullanıcılar
          </button>
        )}
        <div className="tab-active-users" title="Son 15 dakikada işlem yapan kullanıcı sayısı">
          <i className="fa-solid fa-circle" style={{color: activeUsers === 0 ? '#198754' : '#c8a84b', fontSize: 8}} />
          {activeUsers === null ? '—' : activeUsers} aktif
        </div>
        <button
          className="tab-refresh-btn"
          title="Listeyi Yenile"
          onClick={() => { loadData(); if (activeTab === 'users') loadUsers(); }}
        >
          <i className="fa-solid fa-rotate-right" />
        </button>
      </div>

      {/* ── REZERVASYONLAR ── */}
      {activeTab === 'reservations' && (
        <div className="admin-content">
          <div className="res-filter-bar">
            <select value={filterEvent} onChange={e => setFilterEvent(e.target.value)}>
              <option value="all">Tüm etkinlikler</option>
              {events.map(ev => <option key={ev.id} value={ev.id}>{ev.name}</option>)}
            </select>
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
              <option value="active">Aktif ({activeCount})</option>
              <option value="cancelled">İptal ({cancelledCount})</option>
              <option value="all">Tümü ({reservations.length})</option>
            </select>
            <select value={filterPayment} onChange={e => setFilterPayment(e.target.value)}>
              <option value="all">Tüm ödemeler</option>
              <option value="paid">Ödendi</option>
              <option value="pending">Bekliyor ({pendingCount})</option>
            </select>
            <select value={filterManual} onChange={e => setFilterManual(e.target.value)}>
              <option value="all">Tüm satışlar</option>
              <option value="manual">Manuel ({manualCount})</option>
              <option value="online">Online</option>
            </select>
            <input
              type="text"
              placeholder="Müşteri, email veya koltuk..."
              value={filterSearch}
              onChange={e => setFilterSearch(e.target.value)}
            />
            <span className="res-filter-count">{filtered.length} rezervasyon</span>
          </div>

          {filtered.length === 0 ? (
            <div className="empty-state">Rezervasyon bulunamadı.</div>
          ) : (
            grouped.slice((currentPage-1)*PAGE_SIZE, currentPage*PAGE_SIZE).map(([eventId, group]) => (
              <div key={eventId} className="res-group">
                <div className="res-group-header" onClick={() => toggleGroup(eventId)}>
                  <span className="res-group-name">{group.eventName}</span>
                  <div className="res-group-meta">
                    <span>{group.reservations.length} rezervasyon</span>
                    {group.reservations.filter(r => r.isManual && r.status !== 2).length > 0 && (
                      <span className="meta-manual">
                        <i className="fa-solid fa-hand" /> {group.reservations.filter(r => r.isManual && r.status !== 2).length} manuel
                      </span>
                    )}
                    {group.reservations.filter(r => !r.paymentReceived && r.status !== 2).length > 0 && (
                      <span className="meta-warning">
                        <i className="fa-solid fa-clock" />{' '}
                        {group.reservations.filter(r => !r.paymentReceived && r.status !== 2).length} ödeme bekliyor
                      </span>
                    )}
                  </div>
                  <span className={`res-chevron ${collapsed[eventId] ? '' : 'open'}`}>
                    <i className="fa-solid fa-chevron-down" />
                  </span>
                </div>

                {!collapsed[eventId] && (
                  <div className="res-table-wrap">
                    <table className="res-table">
                      <thead>
                        <tr>
                          <th style={{width:'70px'}}>#</th>
                          <th style={{width:'150px'}}>Müşteri</th>
                          <th style={{width:'130px'}}>Koltuklar</th>
                          <th style={{width:'80px'}}>Tutar</th>
                          <th style={{width:'90px'}}>Durum</th>
                          <th style={{width:'100px'}}>Ödeme</th>
                          <th style={{width:'60px'}}>Tip</th>
                          <th style={{width:'140px'}}>İşlem</th>
                        </tr>
                      </thead>
                      <tbody>
                        {group.reservations.map(r => {
                          const status  = getStatusLabel(r.status);
                          const payment = getPaymentLabel(r);
                          const seats   = (r.seats || []).map(s => s.seatId).join(', ');
                          return (
                            <tr key={r.id} className={r.status === 2 ? 'row-cancelled' : ''}>
                              <td className="cell-id">#{r.id?.slice(-6)}</td>
                              <td>
                                <div className="cell-name">{r.customerName}</div>
                                <div className="cell-sub">{r.customerEmail}</div>
                              </td>
                              <td><span className="seats-pill">{seats}</span></td>
                              <td className="cell-amount">{formatCurrency(r.totalAmount)}</td>
                              <td><span className={`res-badge badge-${status.cls}`}>{status.text}</span></td>
                              <td><span className={`payment-label payment-${payment.cls}`}>{payment.text}</span></td>
                              <td>
                                {r.isManual
                                  ? <span className="manual-badge" title="Manuel Satış"><i className="fa-solid fa-hand" /></span>
                                  : <span className="online-badge" title="Online"><i className="fa-solid fa-globe" /></span>}
                              </td>
                              <td>
                                <div className="res-actions">
                                  {!r.paymentReceived && r.status !== 2 && (
                                    <button className="res-btn btn-pay" onClick={() => setPayingReservation(r)}>
                                      <i className="fa-solid fa-credit-card" /> Ödeme Al
                                    </button>
                                  )}
                                  {r.status !== 2 && (
                                    <button className="res-btn btn-cancel" onClick={() => handleCancel(r)}>
                                      <i className="fa-solid fa-ban" /> İptal
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ))
          )}

          {grouped.length > PAGE_SIZE && (
            <div className="stats-paging">
              <button className="page-btn" disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>
                <i className="fa-solid fa-chevron-left" /> Önceki
              </button>
              <span className="page-info">{currentPage} / {Math.ceil(grouped.length / PAGE_SIZE)}</span>
              <button className="page-btn" disabled={currentPage >= Math.ceil(grouped.length / PAGE_SIZE)} onClick={() => setCurrentPage(p => p + 1)}>
                Sonraki <i className="fa-solid fa-chevron-right" />
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── ETKİNLİKLER ── */}
      {activeTab === 'events' && (
        <div className="admin-content">
          <h3>Etkinlikler</h3>
          {events.length === 0 ? (
            <div className="empty-state">Henüz etkinlik yok.</div>
          ) : (
            <div className="events-grid">
              {events.map(event => (
                <div key={event.id} className="event-admin-card">
                  <div className="event-admin-header">
                    <div className="event-admin-title-row">
                      <h4>{event.name}</h4>
                      <span className={`event-type-badge ${parseInt(event.eventType) === 1 ? 'reservation' : 'ticket-sale'}`}>
                        {parseInt(event.eventType) === 1
                          ? <><i className="fa-solid fa-clipboard-list" /> Rezervasyon</>
                          : <><i className="fa-solid fa-ticket" /> Bilet Satışı</>}
                      </span>
                    </div>
                    <p className="event-date">
                      <i className="fa-solid fa-calendar" /> {new Date(event.date).toLocaleString('tr-TR')}
                    </p>
                  </div>
                  <div className="event-admin-body">
                    <p className="event-venue">
                      <i className="fa-solid fa-building-columns" /> {venueMap[event.venueId] || 'Salon bilgisi yok'}
                    </p>
                    {event.description && <p className="event-description">{event.description}</p>}
                    <span className={`event-status ${event.isActive ? 'active' : 'inactive'}`}>
                      {event.isActive
                        ? <><i className="fa-solid fa-circle-check" /> Aktif</>
                        : <><i className="fa-solid fa-circle-xmark" /> Pasif</>}
                    </span>
                  </div>
                  <div className="admin-actions">
                    <button onClick={() => setManagingCodesFor(event)} className="action-btn codes-btn">
                      <i className="fa-solid fa-key" /> Erişim Kodları
                    </button>
                    <button onClick={() => handleManualReservation(event)} className="action-btn manual-btn">
                      <i className="fa-solid fa-hand-point-up" /> Manuel Rezervasyon
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── İSTATİSTİKLER ── */}
      {activeTab === 'stats' && isAdmin && (
        <div className="admin-content">
          <div className="stat-filter-bar">
            <div className="stat-filter-row">
              <div className="stat-filter-item">
                <label>Etkinlik</label>
                <select value={statEvent} onChange={e => setStatEvent(e.target.value)}>
                  <option value="all">Tüm etkinlikler</option>
                  {events.map(ev => <option key={ev.id} value={ev.id}>{ev.name}</option>)}
                </select>
              </div>
              <div className="stat-filter-item">
                <label>Durum</label>
                <select value={statStatus} onChange={e => setStatStatus(e.target.value)}>
                  <option value="all">Tümü</option>
                  <option value="active">Aktif</option>
                  <option value="cancelled">İptal</option>
                </select>
              </div>
              <div className="stat-filter-item">
                <label>Ödeme Durumu</label>
                <select value={statPayment} onChange={e => setStatPayment(e.target.value)}>
                  <option value="all">Tümü</option>
                  <option value="paid">Ödendi</option>
                  <option value="pending">Bekliyor</option>
                </select>
              </div>
              <div className="stat-filter-item">
                <label>Ödeme Şekli</label>
                <select value={statPayMethod} onChange={e => setStatPayMethod(e.target.value)}>
                  <option value="all">Tümü</option>
                  <option value="0">Nakit</option>
                  <option value="1">Kredi Kartı</option>
                  <option value="2">Havale/EFT</option>
                  <option value="3">Online</option>
                  <option value="4">Ücretsiz</option>
                </select>
              </div>
              <div className="stat-filter-item">
                <label>Satış Tipi</label>
                <select value={statManual} onChange={e => setStatManual(e.target.value)}>
                  <option value="all">Tümü</option>
                  <option value="manual">Manuel</option>
                  <option value="online">Online</option>
                </select>
              </div>
              <div className="stat-filter-item">
                <label>Tarih Başlangıç</label>
                <input type="date" value={statDateFrom} onChange={e => setStatDateFrom(e.target.value)} />
              </div>
              <div className="stat-filter-item">
                <label>Tarih Bitiş</label>
                <input type="date" value={statDateTo} onChange={e => setStatDateTo(e.target.value)} />
              </div>
              <div className="stat-filter-item stat-filter-reset">
                <label>&nbsp;</label>
                <button onClick={() => {
                  setStatEvent('all'); setStatStatus('all'); setStatPayment('all');
                  setStatPayMethod('all'); setStatManual('all'); setStatDateFrom(''); setStatDateTo('');
                }}>
                  <i className="fa-solid fa-rotate-left" /> Sıfırla
                </button>
              </div>
            </div>
          </div>

          <div className="stat-filter-result">
            <strong>{filteredStats.length}</strong> rezervasyon seçili
            {filteredStats.length !== reservations.length && <span> ({reservations.length} toplam)</span>}
            {' '}— Manuel: <strong>{filteredStats.filter(r => r.isManual).length}</strong>
            {' '}/ Online: <strong>{filteredStats.filter(r => !r.isManual).length}</strong>
          </div>

          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-icon"><i className="fa-solid fa-list-check" /></div>
              <div className="stat-value">{filteredStats.filter(r=>r.status!==2).length}</div>
              <div className="stat-label">Aktif Rezervasyon</div>
            </div>
            <div className="stat-card">
              <div className="stat-icon"><i className="fa-solid fa-ticket" /></div>
              <div className="stat-value">{filteredStats.filter(r=>r.status!==2).reduce((s,r)=>s+(r.seats?.length||0),0)}</div>
              <div className="stat-label">Aktif Bilet</div>
            </div>
            <div className="stat-card">
              <div className="stat-icon"><i className="fa-solid fa-hand" /></div>
              <div className="stat-value">{filteredStats.filter(r=>r.isManual&&r.status!==2).length}</div>
              <div className="stat-label">Manuel Satış</div>
            </div>
            <div className="stat-card">
              <div className="stat-icon"><i className="fa-solid fa-circle-check" /></div>
              <div className="stat-value">{filteredStats.filter(r=>r.paymentReceived).length}</div>
              <div className="stat-label">Ödeme Alındı</div>
            </div>
            <div className="stat-card">
              <div className="stat-icon"><i className="fa-solid fa-clock" /></div>
              <div className="stat-value">{filteredStats.filter(r=>!r.paymentReceived&&r.status!==2).length}</div>
              <div className="stat-label">Ödeme Bekliyor</div>
            </div>
            <div className="stat-card">
              <div className="stat-icon"><i className="fa-solid fa-ban" /></div>
              <div className="stat-value">{filteredStats.filter(r=>r.status===2).length}</div>
              <div className="stat-label">İptal Edilen</div>
            </div>
          </div>

          <div className="revenue-report">
            <h4><i className="fa-solid fa-coins" /> Gelir Detayları</h4>
            <div className="revenue-grid">
              <div className="revenue-card total">
                <div className="revenue-label">Toplam Tutar</div>
                <div className="revenue-value">{formatCurrency(filteredStats.filter(r=>r.status!==2).reduce((s,r)=>s+(r.totalAmount||0),0), true)}</div>
                <div className="revenue-desc">İptal hariç</div>
              </div>
              <div className="revenue-card paid">
                <div className="revenue-label">Tahsil Edilen</div>
                <div className="revenue-value">{formatCurrency(filteredStats.filter(r=>r.paymentReceived).reduce((s,r)=>s+(r.paidAmount||0),0), true)}</div>
                <div className="revenue-desc">Ödeme alınan</div>
              </div>
              <div className="revenue-card pending">
                <div className="revenue-label">Bekleyen</div>
                <div className="revenue-value">{formatCurrency(filteredStats.filter(r=>!r.paymentReceived&&r.status!==2).reduce((s,r)=>s+(r.totalAmount||0),0), true)}</div>
                <div className="revenue-desc">Ödeme bekleyen</div>
              </div>
              <div className="revenue-card">
                <div className="revenue-label">Ortalama Sepet</div>
                <div className="revenue-value">₺{filteredStats.filter(r=>r.status!==2).length > 0
                  ? (filteredStats.filter(r=>r.status!==2).reduce((s,r)=>s+(r.totalAmount||0),0) / filteredStats.filter(r=>r.status!==2).length).toFixed(2)
                  : '0.00'}</div>
                <div className="revenue-desc">Rezervasyon başına</div>
              </div>
            </div>
          </div>

          {statEvent === 'all' && (
            <div className="stat-event-table">
              <h4><i className="fa-solid fa-masks-theater" /> Etkinlik Bazlı Dağılım</h4>
              <table className="res-table">
                <thead>
                  <tr>
                    <th>Etkinlik</th>
                    <th style={{width:'100px'}}>Rezervasyon</th>
                    <th style={{width:'70px'}}>Bilet</th>
                    <th style={{width:'70px'}}>Manuel</th>
                    <th style={{width:'100px'}}>Tahsilat</th>
                    <th style={{width:'100px'}}>Bekleyen</th>
                    <th style={{width:'70px'}}>İptal</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const map = {};
                    filteredStats.forEach(r => {
                      if (!map[r.eventId]) map[r.eventId] = { name: r.eventName, res: [], cancelled: 0 };
                      if (r.status === 2) map[r.eventId].cancelled++;
                      else map[r.eventId].res.push(r);
                    });
                    return Object.values(map).map((ev, i) => (
                      <tr key={i}>
                        <td style={{fontWeight:500}}>{ev.name}</td>
                        <td>{ev.res.length}</td>
                        <td>{ev.res.reduce((s,r)=>s+(r.seats?.length||0),0)}</td>
                        <td style={{color:'#6b6b7b'}}>{ev.res.filter(r=>r.isManual).length}</td>
                        <td style={{color:'#198754',fontWeight:600}}>{formatCurrency(ev.res.filter(r=>r.paymentReceived).reduce((s,r)=>s+(r.paidAmount||0),0))}</td>
                        <td style={{color:'#856404'}}>{formatCurrency(ev.res.filter(r=>!r.paymentReceived).reduce((s,r)=>s+(r.totalAmount||0),0))}</td>
                        <td style={{color:'#dc3545'}}>{ev.cancelled}</td>
                      </tr>
                    ));
                  })()}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── KULLANICILAR ── */}
      {activeTab === 'users' && isAdmin && (
        <div className="admin-content">
          <div className="res-filter-bar">
            <input
              type="text"
              placeholder="Ad, e-posta veya telefon..."
              value={userSearch}
              onChange={e => setUserSearch(e.target.value)}
              style={{maxWidth: 320}}
            />
            <span className="res-filter-count">
              {users.filter(u => {
                if (!userSearch) return true;
                const q = userSearch.toLowerCase();
                return u.fullName?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q) || u.phone?.includes(q);
              }).length} kullanıcı
            </span>
            <button
              className="res-btn btn-pay"
              style={{marginLeft: 'auto', whiteSpace: 'nowrap'}}
              onClick={() => setShowAddUser(true)}
            >
              <i className="fa-solid fa-user-plus" /> Kullanıcı Ekle
            </button>
          </div>

          {usersLoading ? (
            <div className="loading"><i className="fa-solid fa-spinner fa-spin" /> Yükleniyor...</div>
          ) : (
            <div className="res-table-wrap" style={{borderRadius: 4, border: '1px solid #dee2e6'}}>
              <table className="res-table">
                <thead>
                  <tr>
                    <th style={{width: 40}}>#</th>
                    <th>Ad Soyad</th>
                    <th>E-posta</th>
                    <th style={{width: 120}}>Telefon</th>
                    <th style={{width: 100}}>Rol</th>
                    <th style={{width: 90}}>Durum</th>
                    <th style={{width: 80}}>Üyelik</th>
                    <th style={{width: 100}}>İşlem</th>
                  </tr>
                </thead>
                <tbody>
                  {users.filter(u => {
                    if (!userSearch) return true;
                    const q = userSearch.toLowerCase();
                    return u.fullName?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q) || u.phone?.includes(q);
                  }).map(u => (
                    <tr key={u.id}>
                      <td className="cell-id">{u.id}</td>
                      <td><div className="cell-name">{u.fullName}</div></td>
                      <td><div className="cell-sub" style={{color:'#212529'}}>{u.email}</div></td>
                      <td>{u.phone || <span style={{color:'#bbb'}}>—</span>}</td>
                      <td>
                        {editingRole?.id === u.id ? (
                          <select
                            defaultValue={u.role}
                            autoFocus
                            style={{fontSize:12, padding:'2px 6px', borderRadius:4, border:'1px solid #ced4da'}}
                            onChange={async e => {
                              const newRole = parseInt(e.target.value);
                              try {
                                const token = localStorage.getItem('gg_access_token');
                                const res = await fetch(`${API_URL}/api/users/${u.id}/role`, {
                                  method: 'PUT',
                                  headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                                  body: JSON.stringify({ role: newRole })
                                });
                                if (!res.ok) throw new Error();
                                toast.success('Rol güncellendi!');
                                setEditingRole(null);
                                loadUsers();
                              } catch { toast.error('Rol güncellenemedi!'); }
                            }}
                            onBlur={() => setEditingRole(null)}
                          >
                            <option value={0}>Customer</option>
                            <option value={1}>EventOwner</option>
                            <option value={2}>Admin</option>
                          </select>
                        ) : (
                          <span
                            className={`res-badge ${u.role === 'Admin' ? 'badge-confirmed' : u.role === 'EventOwner' ? 'badge-completed' : 'badge-pending'}`}
                            style={{cursor:'pointer'}}
                            title="Değiştirmek için tıkla"
                            onClick={() => setEditingRole({ id: u.id, role: u.role })}
                          >
                            {u.role}
                          </span>
                        )}
                      </td>
                      <td>
                        <span className={`res-badge ${u.isActive ? 'badge-confirmed' : 'badge-cancelled'}`}>
                          {u.isActive ? 'Aktif' : 'Pasif'}
                        </span>
                      </td>
                      <td style={{fontSize:11, color:'#6c757d'}}>
                        {u.creDate ? new Date(u.creDate).toLocaleDateString('tr-TR') : '—'}
                      </td>
                      <td>
                        <div className="res-actions">
                          <button
                            className="res-btn btn-pay"
                            title="Rol Değiştir"
                            onClick={() => setEditingRole({ id: u.id, role: u.role })}
                          >
                            <i className="fa-solid fa-user-gear" /> Rol
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {users.length === 0 && <div className="empty-state">Kullanıcı bulunamadı.</div>}
            </div>
          )}
        </div>
      )}

      {showAddUser && (
        <AddUserModal
          apiUrl={API_URL}
          onClose={() => setShowAddUser(false)}
          onSuccess={() => { setShowAddUser(false); loadUsers(); }}
        />
      )}

      {payingReservation && (
        <PaymentModal
          reservation={payingReservation}
          onClose={() => setPayingReservation(null)}
          onSuccess={() => { setPayingReservation(null); loadData(); }}
        />
      )}
    </div>
  );
};

const PaymentModal = ({ reservation, onClose, onSuccess }) => {
  const [form, setForm] = useState({ paymentMethod: 0, paidAmount: reservation.totalAmount || 0 });
  const [saving, setSaving] = useState(false);

  const methods = [
    {value:0,label:'Nakit'},{value:1,label:'Kredi Kartı'},
    {value:2,label:'Havale/EFT'},{value:3,label:'Online'},{value:4,label:'Ücretsiz'},
  ];

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.updatePayment(reservation.id, {
        paymentReceived: true,
        paidAmount:      parseFloat(form.paidAmount) || 0,
        paymentMethod:   parseInt(form.paymentMethod),
        paymentDate:     new Date().toISOString(),
      });
      toast.success('Ödeme kaydedildi!');
      onSuccess();
    } catch { toast.error('Ödeme kaydedilemedi!'); }
    finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content payment-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3><i className="fa-solid fa-credit-card" /> Ödeme Al</h3>
          <button onClick={onClose} className="modal-close"><i className="fa-solid fa-xmark" /></button>
        </div>
        <div className="payment-info">
          <p><strong>{reservation.customerName}</strong> — {reservation.eventName}</p>
          <p>Koltuklar: {(reservation.seats||[]).map(s=>s.seatId).join(', ')}</p>
          <p className="payment-total">Toplam: <strong>{formatCurrency(reservation.totalAmount, true)}</strong></p>
        </div>
        <div className="form-group">
          <label>Ödeme Yöntemi</label>
          <select value={form.paymentMethod} onChange={e=>setForm({...form,paymentMethod:e.target.value})}>
            {methods.map(m=><option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label>Alınan Tutar (₺)</label>
          <input type="number" value={form.paidAmount} onChange={e=>setForm({...form,paidAmount:e.target.value})} min="0" step="0.01" />
        </div>
        <div className="modal-actions">
          <button onClick={onClose} className="cancel-btn" disabled={saving}>
            <i className="fa-solid fa-xmark" /> İptal
          </button>
          <button onClick={handleSave} className="submit-btn" disabled={saving}>
            {saving
              ? <><i className="fa-solid fa-spinner fa-spin" /> Kaydediliyor...</>
              : <><i className="fa-solid fa-floppy-disk" /> Ödemeyi Kaydet</>}
          </button>
        </div>
      </div>
    </div>
  );
};

const AddUserModal = ({ onClose, onSuccess, apiUrl }) => {
  const [form, setForm] = useState({ fullName: '', email: '', phone: '', password: '', role: 0 });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form.fullName || !form.email || !form.password) {
      toast.error('Ad Soyad, e-posta ve şifre zorunludur.');
      return;
    }
    if (form.password.length < 8) {
      toast.error('Şifre en az 8 karakter olmalıdır.');
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('gg_access_token');
      const res = await fetch(`${apiUrl}/api/users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(form)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Kullanıcı oluşturulamadı.');
      toast.success('Kullanıcı oluşturuldu!');
      onSuccess();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose} style={{alignItems: 'flex-start', paddingTop: 40, overflowY: 'auto'}}>
      <div className="modal-content" onClick={e => e.stopPropagation()} style={{maxHeight: 'calc(100vh - 80px)', display: 'flex', flexDirection: 'column', margin: 'auto'}}>
        <div className="modal-header">
          <h3><i className="fa-solid fa-user-plus" /> Kullanıcı Ekle</h3>
          <button onClick={onClose} className="modal-close"><i className="fa-solid fa-xmark" /></button>
        </div>
        <div style={{overflowY: 'auto', flex: 1}}>
        <div className="form-group">
          <label>Ad Soyad *</label>
          <input type="text" value={form.fullName} onChange={e => setForm({...form, fullName: e.target.value})} placeholder="Ad Soyad" />
        </div>
        <div className="form-group">
          <label>E-posta *</label>
          <input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} placeholder="ornek@email.com" />
        </div>
        <div className="form-group">
          <label>Telefon</label>
          <input type="text" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} placeholder="5551234567" maxLength={10} />
        </div>
        <div className="form-group">
          <label>Şifre *</label>
          <input type="password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} placeholder="En az 8 karakter" />
        </div>
        <div className="form-group">
          <label>Rol</label>
          <select value={form.role} onChange={e => setForm({...form, role: parseInt(e.target.value)})}>
            <option value={0}>Customer</option>
            <option value={1}>EventOwner</option>
            <option value={2}>Admin</option>
          </select>
        </div>
        </div>
        <div className="modal-actions" style={{position: 'sticky', bottom: 0, background: '#fff', borderTop: '1px solid #dee2e6', marginTop: 'auto'}}>
          <button onClick={onClose} className="cancel-btn" disabled={saving}><i className="fa-solid fa-xmark" /> İptal</button>
          <button onClick={handleSave} className="submit-btn" disabled={saving}>
            {saving ? <><i className="fa-solid fa-spinner fa-spin" /> Kaydediliyor...</> : <><i className="fa-solid fa-floppy-disk" /> Kaydet</>}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;